// src/services/api.js
const API_BASE_URL = process.env.REACT_APP_STRAPI_API_URL;

// Generic API fetch function
const fetchFromAPI = async (endpoint) => {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Categories API
export const getCategories = async () => {
  return await fetchFromAPI('/categories');
};

// Products API
export const getProducts = async (filters = '') => {
  const endpoint = filters ? `/products?${filters}&populate=*` : '/products?populate=*';
  return await fetchFromAPI(endpoint);
};

export const getRelatedProducts = async (productType, pageSize = 12) => {
  if (!productType) {
    return await fetchFromAPI(`/products?populate=*&pagination[pageSize]=${pageSize}`);
  }

  return await fetchFromAPI(`/products?filters[productType][$eq]=${productType}&populate=*&pagination[pageSize]=${pageSize}`);
};

// Get products by type
export const getEyeglasses = async () => {
  return await fetchFromAPI('/products?filters[productType][$eq]=eyeglasses&populate=*&pagination[pageSize]=100');
};

export const getSunglasses = async () => {
  return await fetchFromAPI('/products?filters[productType][$eq]=sunglasses&populate=*&pagination[pageSize]=100');
};

export const getLenses = async () => {
  return await fetchFromAPI('/products?filters[$or][0][productType][$eq]=lenses&filters[$or][1][productType][$eq]=dahab&filters[$or][2][productType][$eq]=sigma&filters[$or][3][productType][$eq]=comfort&populate=*&pagination[pageSize]=100');
};

export const getKids = async () => {
  return await fetchFromAPI('/products?filters[productType][$eq]=kids&populate=*&pagination[pageSize]=100');
};

export const getAccessories = async () => {
  return await fetchFromAPI('/products?filters[productType][$eq]=accessories&populate=*&pagination[pageSize]=100');
};

// Get single product - using filter instead of direct ID access
export const getProduct = async (id) => {
  return await fetchFromAPI(`/products?filters[id][$eq]=${id}&populate=*`);
};

// Get best sellers
export const getBestSellers = async () => {
  return await fetchFromAPI('/products?filters[isBestSeller][$eq]=true&populate=*');
};

// Get products by category
export const getProductsByCategory = async (categorySlug) => {
  return await fetchFromAPI(`/products?filters[category][slug][$eq]=${categorySlug}&populate=*`);
};

// Lens categories API
export const getLensCategories = async () => {
  return await fetchFromAPI('/lens-categories?populate=*');
};

// Reviews API
export const getReviews = async () => {
  return await fetchFromAPI('/reviews?sort=createdAt:desc');
};

export const createReview = async (reviewData) => {
  try {
    console.log('Creating review with data:', reviewData);
    console.log('API URL:', `${API_BASE_URL}/reviews`);
    
    const response = await fetch(`${API_BASE_URL}/reviews`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ data: reviewData }),
    });
    
    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    console.log('Review created successfully:', data);
    return data;
  } catch (error) {
    console.error('API Error creating review:', error);
    throw error;
  }
};

/**
 * Update product quantity in Strapi
 * Note: This requires Strapi API to allow public updates or use authentication
 * @param {number|string} productId - Product ID or documentId
 * @param {number} newQuantity - New quantity value
 * @param {string} documentId - Optional documentId (Strapi v4 uses documentId for updates)
 * @returns {Promise} - Updated product data
 */
export const updateProductQuantity = async (productId, newQuantity, documentId = null) => {
  try {
    // Use documentId if provided, otherwise try productId
    const idToUse = documentId || productId;
    console.log(`[API] Attempting to update product ${productId} (using ID: ${idToUse}) quantity to ${newQuantity}`);
    console.log(`[API] Endpoint: ${API_BASE_URL}/products/${idToUse}`);
    
    const response = await fetch(`${API_BASE_URL}/products/${idToUse}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          quantity: newQuantity,
          inStock: newQuantity > 0 // Auto-update inStock based on quantity
        }
      }),
    });

    console.log(`[API] Response status: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[API] Error response body:', errorText);
      
      // More specific error messages
      if (response.status === 403) {
        throw new Error('Permission denied: Strapi API does not allow public updates. Please enable UPDATE permission for Product content type in Strapi Admin.');
      } else if (response.status === 401) {
        throw new Error('Unauthorized: API token may be required.');
      } else if (response.status === 404) {
        throw new Error(`Product not found (ID: ${idToUse}). The product may have been deleted or the ID is incorrect.`);
      } else {
        throw new Error(`Failed to update product quantity: ${response.status} - ${errorText}`);
      }
    }

    const data = await response.json();
    console.log('[API] Successfully updated product quantity:', data);
    return data;
  } catch (error) {
    console.error('[API] Error updating product quantity:', error);
    throw error;
  }
};

/**
 * Get product by ID (single product for stock checking)
 * @param {number} productId - Product ID
 * @returns {Promise} - Product data
 */
export const getProductById = async (productId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/products/${productId}?populate=*`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('API Error fetching product:', error);
    throw error;
  }
};
